import { browser } from '$app/environment';

export interface AnalyticsConfig {
  surveyId: string;
  domain: string;
  version?: string;
  apiEndpoint: string;
}

export interface AnalyticsEvent {
  surveyId: string;
  domain: string;
  sessionId: string;
  visitorId: string | null;
  path: string;
  referrer: string | null;
  browser: { name: string; version: string };
  os: { name: string; version: string };
  device: { type: string; width: number; height: number };
  language: string;
  eventType: 'pageview' | 'duration_update';
  version?: string;
  duration?: number;
}

// Cookie name for analytics consent
const ANALYTICS_CONSENT_COOKIE = 'moui_analytics_consent';
// Session storage key for rejection
const ANALYTICS_REJECTED_KEY = 'moui_analytics_rejected';
// Session storage key for session ID
const ANALYTICS_SESSION_KEY = 'moui_analytics_session';

/**
 * Analytics service for tracking page views
 * Only tracks when user has granted analytics consent
 */
class AnalyticsService {
  private static instance: AnalyticsService;
  private initialized = false;
  private config: AnalyticsConfig | null = null;
  private sessionId: string | null = null;
  private visitorId: string | null = null;
  private currentPageStartTime: number = 0;
  private currentPath: string = '';

  static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  private constructor() {
    // Private constructor for singleton
  }

  /**
   * Check if analytics consent has been granted (cookie exists with 'granted' value)
   */
  hasConsent(): boolean {
    if (!browser) return false;

    const cookies = document.cookie.split(';');
    const consentCookie = cookies.find(c => c.trim().startsWith(`${ANALYTICS_CONSENT_COOKIE}=`));

    if (consentCookie) {
      const value = consentCookie.split('=')[1]?.trim();
      return value === 'granted';
    }

    return false;
  }

  /**
   * Check if analytics was rejected in current session
   */
  wasRejected(): boolean {
    if (!browser) return false;
    return sessionStorage.getItem(ANALYTICS_REJECTED_KEY) === 'true';
  }

  /**
   * Check if user needs to be shown the consent banner
   * Returns true if no consent decision has been made yet
   */
  needsConsentDecision(): boolean {
    if (!browser) return false;

    // If they already consented, no need for banner
    if (this.hasConsent()) return false;

    // If they rejected this session, no need for banner
    if (this.wasRejected()) return false;

    // Otherwise, they need to make a decision
    return true;
  }

  /**
   * Initialize analytics service with configuration
   * Only initializes if consent has been granted, unless skipConsent is true
   */
  initialize(config: AnalyticsConfig, options?: { skipConsent?: boolean }): boolean {
    if (!browser) return false;
    if (!options?.skipConsent) {
      if (!this.hasConsent()) return false;
      if (this.wasRejected()) return false;
    }

    this.config = config;
    this.sessionId = this.getOrCreateSessionId();
    this.visitorId = this.generateVisitorId();
    this.initialized = true;

    // Set up page visibility tracking for duration
    this.setupDurationTracking();

    return true;
  }

  /**
   * Check if service is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Track a page view
   */
  trackPageView(path: string): void {
    if (!this.initialized || !this.config) return;

    // Deduplicate: don't track the same path twice in a row
    if (this.currentPath === path) return;

    // Update duration for previous page before tracking new one
    if (this.currentPath && this.currentPageStartTime) {
      this.updateDuration();
    }

    this.currentPath = path;
    this.currentPageStartTime = Date.now();

    const event: AnalyticsEvent = {
      surveyId: this.config.surveyId,
      domain: this.config.domain,
      sessionId: this.sessionId!,
      visitorId: this.visitorId,
      path: path,
      referrer: document.referrer || null,
      browser: this.getBrowserInfo(),
      os: this.getOsInfo(),
      device: this.getDeviceInfo(),
      language: navigator.language,
      eventType: 'pageview',
      version: this.config.version
    };

    this.sendEvent(event);
  }

  /**
   * Send duration update for current page
   */
  private updateDuration(): void {
    if (!this.initialized || !this.config || !this.currentPath) return;

    const duration = Date.now() - this.currentPageStartTime;

    const event: Partial<AnalyticsEvent> = {
      surveyId: this.config.surveyId,
      sessionId: this.sessionId!,
      path: this.currentPath,
      eventType: 'duration_update',
      duration: duration
    };

    this.sendEvent(event as AnalyticsEvent);
  }

  /**
   * Set up page visibility tracking to capture duration on page leave
   * Only sends duration update once per page on actual unload (not tab switches)
   */
  private setupDurationTracking(): void {
    if (!browser) return;

    // Only track on actual page unload (navigation, close tab)
    // Using pagehide instead of beforeunload for better reliability with sendBeacon
    window.addEventListener('pagehide', () => {
      this.updateDuration();
    });
  }

  /**
   * Send event to analytics endpoint
   */
  private async sendEvent(event: AnalyticsEvent): Promise<void> {
    if (!this.config) return;

    try {
      const payload = JSON.stringify(event);

      // Use fetch with keepalive for reliable CORS handling
      // sendBeacon() doesn't consistently send Origin header, causing CORS issues
      await fetch(this.config.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
        mode: 'cors'
      });
    } catch (err) {
      // Silent fail - analytics should never break the app
      console.error('[Analytics] Send failed:', err);
    }
  }

  /**
   * Get or create session ID from sessionStorage
   */
  private getOrCreateSessionId(): string {
    let sessionId = sessionStorage.getItem(ANALYTICS_SESSION_KEY);
    if (!sessionId) {
      sessionId = crypto.randomUUID();
      sessionStorage.setItem(ANALYTICS_SESSION_KEY, sessionId);
    }
    return sessionId;
  }

  /**
   * Generate a session-scoped visitor ID (no cross-session tracking)
   * Uses random UUID stored in sessionStorage for GDPR/ePrivacy compliance
   */
  private generateVisitorId(): string | null {
    try {
      const storageKey = 'moui_analytics_visitor';
      let visitorId = sessionStorage.getItem(storageKey);
      if (!visitorId) {
        visitorId = crypto.randomUUID();
        sessionStorage.setItem(storageKey, visitorId);
      }
      return visitorId;
    } catch {
      return null;
    }
  }

  /**
   * Get browser name and version
   */
  private getBrowserInfo(): { name: string; version: string } {
    const ua = navigator.userAgent;

    // Check in order of specificity
    if (ua.includes('Firefox')) {
      return { name: 'Firefox', version: ua.match(/Firefox\/(\d+)/)?.[1] || '' };
    }
    if (ua.includes('Edg/')) {
      return { name: 'Edge', version: ua.match(/Edg\/(\d+)/)?.[1] || '' };
    }
    if (ua.includes('Chrome')) {
      return { name: 'Chrome', version: ua.match(/Chrome\/(\d+)/)?.[1] || '' };
    }
    if (ua.includes('Safari')) {
      return { name: 'Safari', version: ua.match(/Version\/(\d+)/)?.[1] || '' };
    }
    return { name: 'Unknown', version: '' };
  }

  /**
   * Get OS name and version
   */
  private getOsInfo(): { name: string; version: string } {
    const ua = navigator.userAgent;

    if (ua.includes('Windows NT 10')) return { name: 'Windows', version: '10' };
    if (ua.includes('Windows NT 11') || ua.includes('Windows 11')) return { name: 'Windows', version: '11' };
    if (ua.includes('Windows')) return { name: 'Windows', version: '' };
    if (ua.includes('Mac OS X')) {
      const version = ua.match(/Mac OS X (\d+[._]\d+)/)?.[1]?.replace('_', '.') || '';
      return { name: 'macOS', version };
    }
    if (ua.includes('Android')) {
      const version = ua.match(/Android (\d+(\.\d+)?)/)?.[1] || '';
      return { name: 'Android', version };
    }
    if (ua.includes('iPhone') || ua.includes('iPad')) {
      const version = ua.match(/OS (\d+[._]\d+)/)?.[1]?.replace('_', '.') || '';
      return { name: 'iOS', version };
    }
    if (ua.includes('Linux')) return { name: 'Linux', version: '' };
    return { name: 'Unknown', version: '' };
  }

  /**
   * Get device type based on screen width
   */
  private getDeviceInfo(): { type: string; width: number; height: number } {
    const width = window.innerWidth;
    let type = 'desktop';
    if (width < 768) type = 'mobile';
    else if (width < 1024) type = 'tablet';
    return { type, width, height: window.innerHeight };
  }

  /**
   * Reset the service (for testing)
   */
  reset(): void {
    this.initialized = false;
    this.config = null;
    this.sessionId = null;
    this.visitorId = null;
    this.currentPageStartTime = 0;
    this.currentPath = '';
  }
}

// Export singleton instance
export const analyticsService = AnalyticsService.getInstance();
